// @ts-nocheck
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TagWrapper, { TagWrapperProps } from "@store/Library/Components/Toolkit/TagWrapper";
import Tag from "@store/MobiusOverrides/Tag/Tag";
import { getPersonas } from "@store/Framework/Services/CreateAccountService";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    DynamicDropdownProps,
    OptionObject,
} from "@store/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

/** The Personas system list value that turns on the free-text follow-up. */
export const otherJobTitleKey = "Other";

export interface ResetPasswordJobTitleFieldStyles {
    dynamicDropdown?: DynamicDropdownPresentationProps;
    gridItem?: GridItemProps;
    otherGridItem?: GridItemProps;
    otherTextField?: TextFieldPresentationProps;
    tagWrapper?: TagWrapperProps;
}

export interface ResetPasswordJobTitleFieldPresentationProps
    extends ExpandStyles<ResetPasswordJobTitleFieldStyles>,
        DynamicDropdownPresentationProps {}

export interface ResetPasswordJobTitleFieldProps
    extends Partial<Omit<DynamicDropdownProps, "isLoading" | "onSelectionChange" | "options" | "selected">>,
        ResetPasswordJobTitleFieldPresentationProps {
    onChange: (value: string[]) => void;
    onOtherChange: (value: string) => void;
    /** Validation error for the free-text follow-up, supplied by the owning form. */
    otherError?: ReactNode;
    /** Free text captured when "Other" is among the chosen values. */
    otherValue: string;
    /** Chosen Personas keys. */
    value: string[];
}

export const resetPasswordJobTitleFieldStyles: ResetPasswordJobTitleFieldStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    otherGridItem: { width: 12 },
};

/**
 * The Job Title control: a multi-select over the Personas system list, the chosen values as removable
 * tags, and a free-text field when "Other" is picked.
 *
 * Owns the options (fetching, loading and load-failure reporting) and their presentation. The values
 * and their validation stay with the owning form, so one place decides whether the form can submit.
 */
const ResetPasswordJobTitleField: FC<ResetPasswordJobTitleFieldProps> = ({
    error,
    extendedStyles,
    onChange,
    onOtherChange,
    otherError,
    otherValue,
    overridedStyles,
    value,
    ...otherDynamicDropdownProps
}) => {
    const styles = useExpandProps(resetPasswordJobTitleFieldStyles, extendedStyles, overridedStyles);
    const [personas, setPersonas] = useState<OptionObject[]>([]);
    const [loadError, setLoadError] = useState<ReactNode>("");

    useEffect(() => {
        if (personas.length) {
            return;
        }

        getPersonas()
            .then(result => {
                setPersonas(result.map(x => ({ optionValue: x.key, optionText: x.displayName })));
            })
            .catch(() => {
                setLoadError(siteMessage("CreateNewAccountInfo_PersonaFailedToLoad"));
            });
    }, [personas]);

    const availableOptions = useMemo(
        () => personas.filter(x => !x.optionValue || !value.includes(x.optionValue)),
        [personas, value],
    );

    const selectedPersonas = useMemo(
        () => personas.filter(x => !!x.optionValue && value.includes(x.optionValue)),
        [personas, value],
    );

    const isOther = value.includes(otherJobTitleKey);

    const addJobTitle = (jobTitle?: string) => {
        if (jobTitle && !value.includes(jobTitle)) {
            onChange([...value, jobTitle]);
        }
    };

    const removeJobTitle = (jobTitle?: string) => {
        if (!jobTitle) {
            return;
        }

        onChange(value.filter(x => x !== jobTitle));

        if (jobTitle === otherJobTitleKey) {
            onOtherChange("");
        }
    };

    return (
        <>
            <GridItem {...styles.gridItem}>
                <DynamicDropdown
                    {...styles.dynamicDropdown}
                    error={error || loadError}
                    isLoading={!personas.length && !loadError}
                    key={value.length} // Force remount on each selection so the field clears itself;
                    label={translate("Job Title")}
                    onSelectionChange={addJobTitle}
                    options={availableOptions}
                    placeholder={translate("Please add the job title that apply to you.")}
                    required
                    selected=""
                    {...otherDynamicDropdownProps}
                />
                {selectedPersonas.length > 0 && (
                    <TagWrapper showTopGap {...styles.tagWrapper}>
                        {selectedPersonas.map(persona => (
                            <Tag key={persona.optionValue} onDelete={() => removeJobTitle(persona.optionValue)}>
                                {persona.optionText}
                            </Tag>
                        ))}
                    </TagWrapper>
                )}
            </GridItem>
            {isOther && (
                <GridItem {...styles.otherGridItem}>
                    <TextField
                        {...styles.otherTextField}
                        error={otherError}
                        label={translate("Other Job Title")}
                        onChange={event => onOtherChange(event.target.value)}
                        required
                        value={otherValue}
                    />
                </GridItem>
            )}
        </>
    );
};

export default ResetPasswordJobTitleField;
